/**
 * The sign-in gate as a pure function (ADR-0016), so every state is unit-tested.
 *
 *   welcome/sign-in ──signed in──► claim? ──► restoring ──► onboarding? ──► app
 *         ▲                                       │ fails
 *         │ explicit sign-out                     ▼
 *         └──────────────────────────────── restore_failed (retry; never onboarding)
 *
 * Soft gate: a device whose profile is already claimed keeps working when the session lapses (offline,
 * expiry). Logging continues and a banner asks to sign in again. Only an explicit sign-out re-arms the hard gate.
 */
export type GateState =
  | { kind: 'loading' }
  | { kind: 'welcome' }
  | { kind: 'claim' }
  | { kind: 'restoring' }
  | { kind: 'restore_failed' }
  | { kind: 'onboarding' }
  | { kind: 'app'; reauth: boolean };

export interface GateInputs {
  /** False only in development builds with no Supabase configured: no gate there. */
  authConfigured: boolean;
  ready: boolean;
  signedIn: boolean;
  /** The active profile on this device belongs to some user (it was signed in here before). */
  profileClaimed: boolean;
  /** The user explicitly signed out on this device (re-arms the hard gate). */
  signedOut: boolean;
  /** Engine says local, unclaimed data needs the user's decision. */
  needsClaim: boolean;
  pulledOnce: boolean;
  restoreFailed: boolean;
  programmeStarted: boolean;
}

export function gateState(i: GateInputs): GateState {
  if (!i.ready) return { kind: 'loading' };
  if (!i.authConfigured) return i.programmeStarted ? { kind: 'app', reauth: false } : { kind: 'onboarding' };
  if (!i.signedIn) {
    // Soft gate: previously signed in here, session lapsed, programme exists → keep training.
    if (i.profileClaimed && !i.signedOut && i.programmeStarted) return { kind: 'app', reauth: true };
    return { kind: 'welcome' };
  }
  if (i.needsClaim) return { kind: 'claim' };
  if (!i.pulledOnce) return i.restoreFailed ? { kind: 'restore_failed' } : { kind: 'restoring' };
  if (!i.programmeStarted) return { kind: 'onboarding' };
  return { kind: 'app', reauth: false };
}
